// Must match the content-length-range of the API's presigned POST for the kind.
const MIN_BYTES = 100;
export const MAX_BYTES = 2_000_000;
export const MASTER_IMAGE_MAX_BYTES = 30_000_000;

export class ImageValidationError extends Error {}

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((b, i) => bytes[offset + i] === b);

const ascii = (text: string) => [...text].map((c) => c.charCodeAt(0));

const detectType = (header: Uint8Array) => {
  if (startsWith(header, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png";
  if (startsWith(header, [0xff, 0xd8, 0xff])) return "image/jpeg";
  if (startsWith(header, ascii("RIFF")) && startsWith(header, ascii("WEBP"), 8)) return "image/webp";
  return null;
};

/**
 * Checks size and format from the file's own bytes, since the browser derives
 * `File.type` from the file name. Returns the content type to upload with.
 */
export const checkImageBytes = (
  size: number,
  header: Uint8Array,
  maxBytes = MAX_BYTES
): string => {
  if (size > maxBytes) {
    throw new ImageValidationError(`Het bestand is groter dan ${maxBytes / 1_000_000} MB.`);
  }
  if (size < MIN_BYTES) {
    throw new ImageValidationError("Het bestand is te klein om een afbeelding te zijn.");
  }
  const type = detectType(header);
  if (!type) {
    throw new ImageValidationError(
      "Het bestand is geen PNG-, JPEG- of WebP-afbeelding."
    );
  }
  return type;
};

export const validateImage = async (file: File, maxBytes = MAX_BYTES): Promise<string> => {
  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const contentType = checkImageBytes(file.size, header, maxBytes);
  try {
    const bitmap = await createImageBitmap(file);
    bitmap.close();
  } catch {
    throw new ImageValidationError("De afbeelding is beschadigd en kan niet gelezen worden.");
  }
  return contentType;
};
